import { createContext, useContext, ReactNode } from 'react';
import type { Model, AIConnectionStatus } from '../types';
import type { GeminiCategory } from '../../../api/catalog/geminiModels';

/**
 * AI configuration context (wave 2.3).
 *
 * Carries every AI-engine configuration value (provider/model selection,
 * thinking mode, model lists, connection status) plus the related actions so
 * that AIControls no longer needs ~25 configuration props drilled through
 * SimpleWISCVSystem. The state itself still lives in useAIIntegration /
 * useAIProvider — SimpleWISCVSystem feeds the provider with the hook values.
 */
export interface AIConfigContextValue {
  // Provider / model selection
  selectedProvider: 'openrouter' | 'gemini';
  setSelectedProvider: (provider: 'openrouter' | 'gemini') => void;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  fallbackModel: string;
  setFallbackModel: (model: string) => void;
  // Thinking mode
  enableThinking: boolean;
  setEnableThinking: (enable: boolean) => void;
  thinkingBudget: number;
  setThinkingBudget: (budget: number) => void;
  // Model lists
  availableModels: Model[];
  geminiModels: Model[];
  /** Gemini catalog categories (optgroups), in display order. */
  geminiCategories?: GeminiCategory[];
  /** Short info for the user, e.g. a saved model that vanished from the catalog. */
  modelNotice?: string | null;
  loadingModels: boolean;
  // Connection status
  aiConnectionStatus: AIConnectionStatus;
  isTestingConnection: boolean;
  // Actions
  testAIConnection: () => void;
  loadFreeModels: () => void;
  loadGeminiModels: () => void;
}

const AIConfigContext = createContext<AIConfigContextValue | null>(null);

export interface AIConfigProviderProps {
  value: AIConfigContextValue;
  children: ReactNode;
}

export const AIConfigProvider = ({ value, children }: AIConfigProviderProps) => (
  <AIConfigContext.Provider value={value}>{children}</AIConfigContext.Provider>
);

/**
 * Returns the AI configuration. Must be called underneath <AIConfigProvider>.
 */
export const useAIConfig = (): AIConfigContextValue => {
  const ctx = useContext(AIConfigContext);
  if (!ctx) {
    throw new Error(
      'useAIConfig: brak AIConfigProvider w drzewie komponentów. ' +
      'Owiń komponent w <AIConfigProvider value={...}> (robi to SimpleWISCVSystem).'
    );
  }
  return ctx;
};
